/**
 * How a question from /getQuestionsByQuestionnaireType is answered, read off the
 * question itself rather than a list of ids.
 *
 * Assessment.js used to hardcode both: free text was question ids 302-305, and
 * multi-select was question 279. Every follow-up added to the intake form since
 * (the preferred-pharmacy and cancer-type questions) has a new id, so it rendered
 * with no text box and no Continue button: a blank card, and a patient who left
 * it was sent back to the start of the intake form. The popup version of this
 * form (SymptomCalculator.js) already reads them off the data with its own copy
 * of these two rules; this is the same rule, tested. Change both together.
 */

/**
 * The intake form's questionnaire, in the diagnosis table. Its answers are saved
 * under this id whatever page led to the form, so the form never depends on being
 * told it: a page refresh used to lose it, and the submission was refused.
 * The server has the same number (PatientService.INTAKE_FORM_DIAGNOSIS_ID).
 */
export const INTAKE_FORM_DIAGNOSIS_ID = 8;

/** A question with no options is answered by typing. */
export const isFreeTextQuestion = (question) => !question?.options?.length;

/** "Check all that apply" questions take several answers. */
export const isMultiSelectQuestion = (question) =>
  typeof question?.question === "string" &&
  question.question.toLowerCase().includes("check all that apply");

/**
 * The option id to send as selected_option_id after a multi-select answer.
 *
 * Only one id travels to the server, and it is what a follow-up question is
 * looked up by. Send a ticked option that has a follow-up behind it, so ticking
 * "Cancer" reaches "What type of cancer?", and 0 when none does.
 *
 * Only the first such option is sent, so a second ticked option with its own
 * follow-up would never be asked. Today only Cancer has one; asking for several
 * would need the server to return each in turn.
 */
export const followUpOptionId = (question, tickedIds = []) => {
  const option = (question?.options || []).find(
    (o) => tickedIds.includes(o.oId) && o.hasFollowUpQuestion
  );
  return option ? option.oId : 0;
};
